import { useCallback, useEffect, useRef } from 'react';
import type { MutableRefObject } from 'react';
import * as Cesium from 'cesium';

export type MeasureMode = 'distance' | 'area';

interface UseMeasureToolArgs {
  isViewerReady: boolean;
  viewerRef: MutableRefObject<Cesium.Viewer | null>;
  /** True while the Measure Tool is the active map tool. */
  active: boolean;
  /** Distance vs Area tab. Switching tabs starts a fresh measurement. */
  mode: MeasureMode;
  /** Emits the cumulative geodesic distance in metres (0 when < 2 points). */
  onDistanceChange: (meters: number) => void;
  /** Emits the geodesic area in square metres (0 when < 3 points). */
  onAreaChange: (squareMeters: number) => void;
  onError: (message: string | null) => void;
}

// All measure geometry lives in a dedicated data source so it is torn down
// cleanly and never clobbered by other tools that call `entities.removeAll()`.
const MEASURE_DATA_SOURCE = 'measure-tool';
// Pixel distance under which a click is treated as "on the first vertex"
// (used to close an area polygon — US-102821 Scenario 5).
const CLOSE_PIXEL_THRESHOLD = 14;
const DRAW_COLOR = '#ff6600';
// WGS84 equatorial radius (metres) — the web-mapping convention for the
// spherical-excess polygon-area approximation below.
const EARTH_RADIUS = 6378137;

/**
 * US-102820 / US-102821 — Measure Tool drawing interaction.
 *
 * Owns its own ScreenSpaceEventHandler and a dedicated CustomDataSource.
 * - Distance: every left-click appends a vertex; the cumulative geodesic
 *   length (committed segments + the live cursor segment) is emitted in real
 *   time. There is no closing — the polyline keeps extending.
 * - Area: left-clicks build a polygon; clicking the first vertex again (with
 *   >= 3 vertices) closes it and freezes the geodesic area. While open, the
 *   area updates live as the cursor moves.
 */

export const CUSTOM_CROSSHAIR = `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='16' cy='16' r='10' stroke='white' stroke-width='2'/%3E%3Cpath d='M16 2V8M16 24V30M2 16H8M24 16H30' stroke='white' stroke-width='2' stroke-linecap='round'/%3E%3Ccircle cx='16' cy='16' r='1.5' fill='white'/%3E%3C/svg%3E") 16 16, crosshair`;

export const useMeasureTool = ({ isViewerReady, viewerRef, active, mode, onDistanceChange, onAreaChange, onError }: UseMeasureToolArgs) => {
  const handlerRef = useRef<Cesium.ScreenSpaceEventHandler | null>(null);
  const pointsRef = useRef<Cesium.Cartesian3[]>([]);
  const mouseRef = useRef<Cesium.Cartesian3 | null>(null);
  const closedRef = useRef(false);

  // Track preview entities so they can be re-created after a reset
  const previewLineRef = useRef<Cesium.Entity | null>(null);
  const previewPolygonRef = useRef<Cesium.Entity | null>(null);
  const closingLineRef = useRef<Cesium.Entity | null>(null);

  const onDistanceChangeRef = useRef(onDistanceChange);
  const onAreaChangeRef = useRef(onAreaChange);
  const onErrorRef = useRef(onError);
  onDistanceChangeRef.current = onDistanceChange;
  onAreaChangeRef.current = onAreaChange;
  onErrorRef.current = onError;

  // Helper to get the correct mode-specific service error string
  const getServiceError = useCallback(() => {
    return mode === 'area' ? 'Area measurement is currently unavailable.' : 'Distance measurement is currently unavailable.';
  }, [mode]);

  const getDataSource = useCallback((viewer: Cesium.Viewer): Cesium.CustomDataSource => {
    const existing = viewer.dataSources.getByName(MEASURE_DATA_SOURCE)[0];
    if (existing) return existing as Cesium.CustomDataSource;
    const ds = new Cesium.CustomDataSource(MEASURE_DATA_SOURCE);
    viewer.dataSources.add(ds);
    return ds;
  }, []);

  // Cumulative geodesic distance over consecutive points (metres).
  const geodesicDistance = (points: Cesium.Cartesian3[]): number => {
    let distance = 0;
    for (let i = 1; i < points.length; i++) {
      const geodesic = new Cesium.EllipsoidGeodesic(Cesium.Cartographic.fromCartesian(points[i - 1]), Cesium.Cartographic.fromCartesian(points[i]));
      distance += geodesic.surfaceDistance;
    }
    return distance;
  };

  // Geodesic polygon area (square metres) via the spherical-excess formula
  // (same approximation OpenLayers/Google use). Cartographic lon/lat are in
  // radians, so they feed the formula directly.
  const geodesicArea = (points: Cesium.Cartesian3[]): number => {
    if (points.length < 3) return 0;
    const carto = points.map((p) => Cesium.Cartographic.fromCartesian(p));
    let total = 0;
    for (let i = 0; i < carto.length; i++) {
      const p1 = carto[i];
      const p2 = carto[(i + 1) % carto.length];
      total += (p2.longitude - p1.longitude) * (2 + Math.sin(p1.latitude) + Math.sin(p2.latitude));
    }
    return Math.abs((total * EARTH_RADIUS * EARTH_RADIUS) / 2);
  };

  // Recompute and emit the measurement. `includeCursor` adds the live cursor
  // position so the value updates as the mouse moves before the next click.
  const emit = useCallback((includeCursor: boolean, currentMode: MeasureMode) => {
    const points = [...pointsRef.current];
    if (includeCursor && mouseRef.current && !closedRef.current) points.push(mouseRef.current);
    try {
      if (currentMode === 'area') {
        onAreaChangeRef.current(geodesicArea(points));
      } else {
        onDistanceChangeRef.current(geodesicDistance(points));
      }
      onErrorRef.current(null); // Clear error on successful calculation
    } catch (err: any) {
      // Scenario 1 & 5: Hide invalid values and show error message
      onAreaChangeRef.current(0);
      onDistanceChangeRef.current(0);
      onErrorRef.current(err.message);
    }
  }, []);

  // Remove all drawn geometry and reset the in-progress sketch state.
  const clearGeometry = useCallback((viewer: Cesium.Viewer) => {
    const ds = viewer.dataSources.getByName(MEASURE_DATA_SOURCE)[0];
    if (ds) ds.entities.removeAll();

    pointsRef.current = [];
    mouseRef.current = null;
    closedRef.current = false;
    onErrorRef.current(null);
    previewLineRef.current = null;
    previewPolygonRef.current = null;
    closingLineRef.current = null; // Reset ref
  }, []);

  // RESET button: wipe geometry but keep the tool armed (US-102820 Sc9).
  const reset = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;
    clearGeometry(viewer);
    onDistanceChangeRef.current(0);
    onAreaChangeRef.current(0);

    // Re-arm cursor
    viewer.scene.canvas.style.cursor = CUSTOM_CROSSHAIR;
  }, [viewerRef, clearGeometry]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!isViewerReady || !viewer) return;

    const teardown = () => {
      handlerRef.current?.destroy();
      handlerRef.current = null;
      if (!viewer.isDestroyed()) {
        clearGeometry(viewer);
        viewer.scene.canvas.style.cursor = '';
      }
    };

    if (!active) {
      teardown();
      return;
    }

    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    // Scenario 2: Service Health Check
    const checkService = () => {
      if (viewer.scene.globe.show === false) {
        onErrorRef.current(getServiceError());
        handlerRef.current?.destroy();
      }
    };
    const removeHealthCheck = viewer.scene.postRender.addEventListener(checkService);

    // Fresh measurement whenever the tool is (re)activated or the tab changes.
    clearGeometry(viewer);
    handlerRef.current?.destroy();

    const ds = getDataSource(viewer);
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handlerRef.current = handler;
    viewer.scene.canvas.style.cursor = CUSTOM_CROSSHAIR;

    const drawColor = Cesium.Color.fromCssColorString(DRAW_COLOR);

    // Live outline: committed vertices + the cursor, closing back to the start
    // for area so the shape reads as a polygon while drawing.
    const ensurePreview = () => {
      if (previewLineRef.current) return;
      previewLineRef.current = ds.entities.add({
        polyline: {
          positions: new Cesium.CallbackProperty(() => {
            const pts = [...pointsRef.current];
            if (mouseRef.current && !closedRef.current) pts.push(mouseRef.current);
            // if (mode === "area" && pts.length >= 2) pts.push(pointsRef.current[0]);
            if (mode === 'area' && pts.length >= 3 && closedRef.current) {
              pts.push(pointsRef.current[0]);
            }
            return pts;
          }, false),
          width: 2,
          material: drawColor,
          clampToGround: false,
        },
      });
      if (mode === 'area') {
        closingLineRef.current = ds.entities.add({
          polyline: {
            positions: new Cesium.CallbackProperty(() => {
              if (pointsRef.current.length > 0 && mouseRef.current && !closedRef.current) {
                return [pointsRef.current[0], mouseRef.current];
              }
              return [];
            }, false),
            width: 2,
            material: new Cesium.PolylineDashMaterialProperty({
              color: drawColor,
              dashLength: 8.0,
            }),
            clampToGround: false,
          },
        });
        previewPolygonRef.current = ds.entities.add({
          polygon: {
            hierarchy: new Cesium.CallbackProperty(() => {
              const pts = [...pointsRef.current];
              if (mouseRef.current && !closedRef.current) pts.push(mouseRef.current);
              return pts.length >= 3 ? new Cesium.PolygonHierarchy(pts) : new Cesium.PolygonHierarchy([]);
            }, false),
            material: drawColor.withAlpha(0.3),
          },
        });
      }
    };

    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      if (closedRef.current) return;
      const cartesian = viewer.camera.pickEllipsoid(click.position, viewer.scene.globe.ellipsoid);
      if (!cartesian) {
        // Scenario 2: Map Service Failure (Outside globe)
        onErrorRef.current(getServiceError());
        return;
      }

      // Area closing click: near the first vertex with at least a triangle.
      if (mode === 'area' && pointsRef.current.length >= 3) {
        const firstWindow = Cesium.SceneTransforms.wgs84ToWindowCoordinates(viewer.scene, pointsRef.current[0]);
        if (firstWindow && Cesium.Cartesian2.distance(firstWindow, click.position) < CLOSE_PIXEL_THRESHOLD) {
          closedRef.current = true;
          mouseRef.current = null;
          emit(false, mode);
          viewer.scene.canvas.style.cursor = '';
          return;
        }
      }

      pointsRef.current.push(cartesian);
      ds.entities.add({
        position: cartesian,
        point: {
          pixelSize: 11,
          color: drawColor,
          outlineWidth: 0,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      ensurePreview();
      emit(false, mode);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Double click to finish
    handler.setInputAction(() => {
      if (mode === 'area') return;
      if (pointsRef.current.length < 2) return;

      // BUG FIX: Only remove the last point if it is an exact spatial duplicate
      // of the second-to-last point (common in double-click sequences).
      if (pointsRef.current.length > 1) {
        const lastPoint = pointsRef.current[pointsRef.current.length - 1];
        const secondLastPoint = pointsRef.current[pointsRef.current.length - 2];

        if (Cesium.Cartesian3.equalsEpsilon(lastPoint, secondLastPoint, Cesium.Math.EPSILON6)) {
          pointsRef.current.pop();
          // Remove the duplicate graphic entity from the map as well
          const entities = ds.entities.values;
          const lastEntity = entities[entities.length - 1];
          if (lastEntity && lastEntity.position) {
            ds.entities.remove(lastEntity);
          }
        }
      }

      closedRef.current = true;
      mouseRef.current = null;
      emit(false, mode);
      viewer.scene.canvas.style.cursor = '';
    }, Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      if (closedRef.current || pointsRef.current.length === 0) return;
      mouseRef.current = viewer.camera.pickEllipsoid(movement.endPosition, viewer.scene.globe.ellipsoid) ?? null;
      emit(true, mode);
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    handler.setInputAction(() => {
      // Right click to cancel
      reset(); // Resets points and geometry
      // Optionally deactivate tool if desired, or just clear current drawing
      onErrorRef.current(null);
    }, Cesium.ScreenSpaceEventType.RIGHT_CLICK);

    // Double-click is already disabled in the existing code:
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);

    return () => {
      teardown();
      removeHealthCheck();
    };
  }, [active, mode, isViewerReady, viewerRef, getDataSource, clearGeometry, emit, getServiceError]);

  // Safety net: detach the handler and remove the data source on unmount.
  useEffect(() => {
    return () => {
      const viewer = viewerRef.current;
      handlerRef.current?.destroy();
      handlerRef.current = null;
      if (viewer && !viewer.isDestroyed()) {
        viewer.scene.canvas.style.cursor = '';
        const ds = viewer.dataSources.getByName(MEASURE_DATA_SOURCE)[0];
        if (ds) viewer.dataSources.remove(ds, true);
      }
    };
  }, [viewerRef]);

  return { reset };
};
